<template>
  <div class="departments-views-selector" role="group" aria-label="Departments view">
    <div
      :class="['view-item', { active: active === 'tree' }]"
      role="button"
      tabindex="0"
      :aria-pressed="String(active === 'tree')"
      aria-label="Structure"
      @click="handleSelection('tree')"
      @keydown.enter="handleSelection('tree')"
      @keydown.space.prevent="handleSelection('tree')"
    >
      <GridIcon :active="active === 'tree'" grey-out-inactive />
    </div>
    <div
      :class="['view-item', { active: active === 'tree-with-positions' }]"
      role="button"
      tabindex="0"
      :aria-pressed="String(active === 'tree-with-positions')"
      aria-label="Browse"
      @click="handleSelection('tree-with-positions')"
      @keydown.enter="handleSelection('tree-with-positions')"
      @keydown.space.prevent="handleSelection('tree-with-positions')"
    >
      <PaneIcon :active="active === 'tree-with-positions'" grey-out-inactive />
    </div>
    <div
      :class="['view-item', { active: active === 'orphan-positions' }]"
      role="button"
      tabindex="0"
      :aria-pressed="String(active === 'orphan-positions')"
      aria-label="Unassigned"
      @click="handleSelection('orphan-positions')"
      @keydown.enter="handleSelection('orphan-positions')"
      @keydown.space.prevent="handleSelection('orphan-positions')"
    >
      <UnassignedIcon :active="active === 'orphan-positions'" grey-out-inactive />
    </div>
    <span
      v-if="orphanPositionsCount >= 1"
      class="badge"
      :aria-label="`${orphanPositionsCount} unassigned positions`"
      >{{ orphanPositionsCount }}</span
    >
  </div>
</template>

<script setup>
import GridIcon from "../PositionsViewSelector/icons/GridIcon.vue";
import PaneIcon from "../PositionsViewSelector/icons/PaneIcon.vue";
import UnassignedIcon from "./icons/UnassignedIcon.vue";

const props = defineProps({
  active: {
    type: String,
    required: true,
    validator: (value) => ["tree", "tree-with-positions", "orphan-positions"].includes(value),
  },
  orphanPositionsCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["input", "update:active", "selection-change"]);

function handleSelection(mode) {
  if (props.active !== mode) {
    emit("input", mode);
    emit("update:active", mode);
    emit("selection-change", mode);
  }
}
</script>

<style scoped>
.departments-views-selector {
  position: relative;
  display: inline-flex;
  height: 44px;
  padding: 2px 3px;
  align-items: center;
  gap: 4.4px;
  border-radius: 10px;
  border: 1px solid var(--Stroke, #d0d5dd);
  background: #fff;
}

.view-item {
  position: relative;
  display: flex;
  padding: 7px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  outline: none;
}

.view-item:focus-visible {
  box-shadow: 0 0 0 2px #7f56d9;
  border-radius: 8.8px;
}

.view-item.active {
  display: flex;
  padding: 7px;
  justify-content: center;
  align-items: center;
  gap: 4.4px;
  border-radius: 8.8px;
  background: var(--Primary, #7f56d9);
  box-shadow:
    2px 2px 1.6px 0 rgba(193, 165, 255, 0.24) inset,
    0 -1px 4px 0 rgba(0, 0, 0, 0.25) inset,
    0 2px 4px 0 rgba(138, 138, 138, 0.25);
  border: none;
}

.view-item.active :deep(path) {
  opacity: 1;
  stroke: #fff;
}

.view-item.active :deep(path[fill]) {
  fill: #fff;
}

.badge {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 1;
  display: flex;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  justify-content: center;
  align-items: center;
  border-radius: 9px;
  background: #d85c32;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  pointer-events: none;
}
</style>
